import { useContext, useRef, useState } from 'react';
import { v4 as generateId } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';

import { ButtonBaseType, ID, Result } from 'src/models';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';
import { useDebouncedFn } from 'src/hooks';
import { requestPlacesAutocomplete } from 'src/store/autocomplete/autocomplete.actions';
import { ifTrue } from 'src/tools/logic.tools';

export function PlacesAutoComplete() {
  const {
    value,
    onChange,
    classNames,
    onDiscard = mockFn,
    isDisabled,
    isAutoFocus,
    placeholder,
    customization = {},
  } = useContext(InputContext);

  const { dropdownMinWidth = 'container-width' } = customization;

  const [results, setResults] = useState<ButtonBaseType[]>([]);

  const [isOpen, setIsOpen] = useState(false);

  const skipResponse = useRef(false);

  const dispatch = useDispatch();

  const isLoading = useSelector(getCustomIsLoading)('placesAutocomplete');

  const [inputValue, setInputValue] = useState(value);

  function handleOnChange(userInput: string) {
    setInputValue(userInput);
    onChange(userInput);

    if (userInput.length > 5) {
      request(userInput);
      skipResponse.current = false;
    } else if (!userInput) {
      setResults([]);
      skipResponse.current = true;
    }
  }

  function handleResults(response: Result) {
    if (skipResponse.current || !response.isOK) {
      return;
    }

    setResults(
      response.payload.map(
        (label: string): ButtonBaseType => ({
          id: generateId(),
          label,
        }),
      ),
    );
    setIsOpen(true);
  }

  const request = useDebouncedFn(sendRequest, 600);

  function sendRequest(newValue: string) {
    if (!skipResponse.current) {
      dispatch(requestPlacesAutocomplete(newValue, { callback: handleResults }));
    }
  }

  function handleOnItemClick(id: ID) {
    setIsOpen(false);

    const resultsItem = results.find(i => i.id === id);

    if (resultsItem) {
      setInputValue(resultsItem.label);
      onChange(resultsItem.label, true);
    }
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnDiscard() {
    setIsOpen(false);

    onDiscard();
  }

  function renderBaseComponent({ onClick, onFocus, onBlur }: BaseComponentProps) {
    const iconId = isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        className={classNames?.input}
        value={inputValue}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={ifTrue(results.length, iconId)}
        onClick={onClick}
        isLoading={isLoading}
        onDiscard={handleOnDiscard}
        onChange={handleOnChange}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={results}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      isOpen={isOpen}
      onRequestOpenClose={handleOnRequestOpenClose}
      dropdownMinWidth={dropdownMinWidth}
    />
  );
}
